import { useTranslation } from "react-i18next";
import { Button, Icon } from "@nexus/ui";
import { ApiError } from "@/lib/api-client";
import { scheduledJobErrorMessage } from "../lib/scheduled-job-errors";

const SCHEDULER_WRITE_DENIED_CODE = "SCHEDULER_WRITE_DENIED";

interface ScheduledJobMutationErrorProps {
  error: unknown;
  onDismiss?: () => void;
}

/**
 * Operator mutation failure — title + body next to the actions that failed,
 * not a wall of prose buried in the scrollable sheet body.
 */
export function ScheduledJobMutationError({
  error,
  onDismiss,
}: ScheduledJobMutationErrorProps) {
  const { t } = useTranslation();

  const isWriteDenied =
    error instanceof ApiError && error.code === SCHEDULER_WRITE_DENIED_CODE;

  const title = isWriteDenied
    ? t("admin.scheduledJobs.writeDenied.title", "Missing Cloud Scheduler write access")
    : t("admin.scheduledJobs.mutationFailedTitle", "Couldn't update this job");

  const body = isWriteDenied
    ? t(
        "admin.scheduledJobs.writeDenied.body",
        "This identity can list jobs but cannot create, update, pause, resume, or delete them. Ask DevOps for roles/cloudscheduler.admin on the scheduler project.",
      )
    : scheduledJobErrorMessage(
        error,
        t(
          "admin.scheduledJobs.mutationFailed",
          "Could not update this job. Check Cloud Scheduler write IAM if the action was pause, resume, save, or delete.",
        ),
      );

  return (
    <div
      role="alert"
      className="flex gap-2.5 rounded-md border border-error-border bg-error-subtle px-3 py-2.5 text-error-subtle-foreground"
    >
      <Icon
        name="alert-circle"
        size="sm"
        color="destructive"
        className="mt-0.5"
        aria-hidden
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="m-0 text-xs font-medium leading-4 tracking-[-0.01em]">{title}</p>
        <p className="m-0 max-w-[65ch] text-[11px] leading-4 opacity-90">{body}</p>
      </div>
      {onDismiss ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="size-7 shrink-0 text-error-subtle-foreground hover:bg-error/10 hover:text-error-subtle-foreground"
          aria-label={t("admin.scheduledJobs.dismissError", "Dismiss error")}
          onClick={onDismiss}
        >
          <Icon name="x" size="sm" color="inherit" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}
